import React from "react"
import styled, { css } from "styled-components"
import quoteImg from "../../images/quote.svg"
import Arrow from "../../images/arrow.inline.svg"

/* layout */
export const gridSpacing = css`
  --grid-spacing: 50px;
  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    --grid-spacing: 80px;
  }
  @media (min-width: 1220px) {
    --grid-spacing: 80px;
  }
`

export const commerceGridSpacing = css`
  --grid-spacing: 20px;
  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    --grid-spacing: 30px;
  }
  @media (min-width: 1220px) {
    --grid-spacing: 35px;
  }
`

/* texts */
export const smallBodyTextStyles = css`
  font-weight: 500;
  letter-spacing: 2px;
  text-decoration: none;
  line-height: ${30 / 13}em;
  text-transform: uppercase;
  font-size: ${13 / 16}rem;
  color: ${({ theme }) => theme.color.text};
  font-family: ${({ theme }) => theme.font.display};
`

export const kickstarterButtonStyles = css`
  background-color: ${({ theme }) => theme.color.kickstarter};
  color: ${({ theme }) => theme.color.textInverted};

  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.color.kickstarterActive};
  }
`

export const annotationStyles = css`
  margin: 0;
  font-weight: 300;
  letter-spacing: 0;
  text-transform: initial;
  line-height: ${14 / 11}em;
  font-size: ${11 / 16}rem;
  color: ${({ theme }) => theme.color.annotation};
  font-family: ${({ theme }) => theme.font.basic};
  @media (min-width: 640px) {
    font-size: ${14 / 16}rem;
    line-height: ${28 / 14}em;
  }
`

export const Annotation = styled.p`
  ${annotationStyles};
`

export const ErrorMessage = styled(Annotation)`
  color: ${({ theme }) => theme.color.error};
  @media (min-width: 640px) {
    font-size: ${12 / 16}rem;
  }
`

export const quoteStyles = css`
  margin: 0;
  display: flex;
  align-self: start;
  border-radius: 5px;
  text-align: center;
  box-sizing: border-box;
  flex-direction: column;
  padding: 0 35px 35px 35px;
  margin-bottom: 40px;
  background-color: ${({ theme }) => theme.color.backgroundAttention};

  &:before {
    width: 100%;
    content: "";
    height: 36px;
    display: block;
    margin-top: -7px;
    margin-bottom: 35px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url(${quoteImg});

    @media (min-width: 768px) {
      height: 50px;
      margin-top: -15px;
      margin-bottom: 50px;
    }
  }

  @media (min-width: 768px) {
    padding: 0 50px 50px 50px;
  }

  cite {
    display: block;
    margin-top: 30px;
    font-style: normal;
    ${smallBodyTextStyles};
    color: ${({ theme }) => theme.color.link};
  }
`

export const Quote = styled.blockquote`
  ${quoteStyles};
`

export const Article = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  max-width: 650px;
  p {
    font-family: Facto;
    font-size: 20px;
    font-weight: 400;
    line-height: 26px;
    text-align: center;
    margin: 0;
    width: 100%;
    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      width: unset;
    }
  }

  a {
    padding: 13px 70px;
    text-align: center;
    font-family: Facto;
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 30px; /* 230.769% */
    letter-spacing: 2px;
    text-transform: uppercase;
    width: 100%;
    @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      width: unset;
    }
  }
`

export const postDateStyles = css`
  ${smallBodyTextStyles};
  color: inherit;
  display: block;
  line-height: 1;
  font-weight: 400;
  margin: 10px 0 20px 0;
  font-size: ${11 / 16}rem;
`

export const PostDate = styled((props) => <time {...props} />)`
  ${postDateStyles};
  padding-top: 8px;
`

/* form elements */
export const Label = styled.span`
  color: inherit;
  padding: 6px 15px;
  margin-left: 15px;
  line-height: 30px;
  white-space: nowrap;
  vertical-align: middle;
  border-radius: ${({ theme }) => theme.size.borderRadius};
  box-shadow: 0 2px 20px 0 ${({ theme }) => theme.color.lightShadow};
  background-color: ${({ theme }) => theme.color.backgroundInverted};
  ${smallBodyTextStyles};
`

export const AnimatedInputLabel = styled(({ focused, empty, ...rest }) => (
  // eslint-disable-next-line jsx-a11y/label-has-associated-control
  <label {...rest} />
))<{
  focused: boolean
  empty: boolean
}>`
  z-index: 2;
  left: 20px;
  cursor: text;
  font-weight: 300;
  position: absolute;
  transition: all 0.1s ease-in-out;
  line-height: 2rem;
  font-size: ${({ focused, empty }) =>
    focused || !empty ? `${12 / 16}rem` : `1rem`};
  top: ${({ focused, empty }) =>
    focused || !empty ? `0` : `calc(50% - 1rem)`};
  color: ${({ theme }) => theme.color.inputPlaceholder};
  @media (max-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    left: 15px;
    font-size: ${({ focused, empty }) =>
      focused || !empty ? `${12 / 16}rem` : `${15 / 16}rem`};
  }
`

/* interactive elements */
export const NavigationArrow = styled(Arrow)<{
  $inactive?: boolean
}>`
  width: 32px;
  height: 32px;
  padding: 9px;
  display: block;
  cursor: pointer;
  border-radius: 50%;
  box-sizing: border-box;
  transition: background-color 0.15s ease-in-out;
  background-color: ${({ theme }) => theme.color.arrowBackgroundLight};

  &:hover {
    background-color: ${({ theme }) => theme.color.arrowBackgroundDark};
  }

  ${({ $inactive }) =>
    $inactive &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `};

  path {
    fill: ${(props) => props.theme.color.arrowFillLight};
  }
`

/* decorators */
export const roundedBox = css`
  overflow: hidden;
  border-radius: ${({ theme }) => theme.size.borderRadius};
`

export const markedTextStyles = css`
  text-decoration: none;
  padding: 0.3em;
  line-height: inherit;
  background: linear-gradient(to top, #eef3f9, #eef3f9 95%, transparent 5%);
  font-weight: 500;
`

export const MarkedText = styled.span`
  ${markedTextStyles};
`

export const footnotesAnchors = css`
  a[href^="#ft-"] {
    position: relative;
    span[id] {
      left: 0;
      top: -90px;
      display: block;
      position: absolute;
      visibility: hidden;
      @media (min-width: ${({ theme }) =>
          theme.breakpoint.tabletHorizontal}px) {
        top: -120px;
      }
    }
  }
`

export const Notification = styled.span`
  position: absolute;
  bottom: 0;
  right: 3px;
  font-size: 10px;
  color: ${({ theme }) => theme.color.textInverted};
  height: 16px;
  width: 16px;
  background-color: ${({ theme }) => theme.color.notification};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 16px;
  letter-spacing: 0;
`

/* styles for ul tag */
export const listStyles = css`
  margin-top: 25px;
  padding-left: 20px;
  list-style: none;
  li {
    margin: 25px 0;
    line-height: 1.8;
    position: relative;
    &::before {
      content: "";
      top: 13px;
      left: -20px;
      width: 2px;
      height: 2px;
      border-radius: 50%;
      display: inline-block;
      position: absolute;
      background-color: black;
    }
  }
`
